// Shared UI kit for the Priority admin registry pages
// (Vendors, Contractors, Vehicles, Supplies, Invoice Dropdowns).
//
// - Every popup renders through createPortal(document.body) with fully inline
//   positioning styles, so the conflicting global `.modal` / `.modal-overlay`
//   rules elsewhere in the app can never move or hide it.
// - Visual styling lives in ./OpsUi.css under the `.opsui-` prefix.
import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { collection, doc, getDoc, getDocs, setDoc } from "firebase/firestore";
import { db } from "../firebaseConfig";
import { receivesVehicleExpirationEmails, isStaffDeactivated } from "../utils/staffSettingsUtils";
import "./OpsUi.css";

// ---------------------------------------------------------------------------
// constants / helpers
// ---------------------------------------------------------------------------
export const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "DC", "FL", "GA", "HI", "ID", "IL",
  "IN", "IA", "KS", "KY", "LA", "ME", "MD", "MA", "MI", "MN", "MS", "MO", "MT",
  "NE", "NV", "NH", "NJ", "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI",
  "SC", "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY",
];

// Parse "YYYY-MM-DD" (or a Firestore Timestamp / Date / ISO string) as a LOCAL
// calendar date — same rules as parseDateOnly() in the Cloud Functions.
export const parseDateOnly = (value) => {
  if (!value) return null;
  if (typeof value?.toDate === "function") {
    const d = value.toDate();
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }
  if (value instanceof Date) {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }
  const str = String(value).trim();
  const m = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const d = new Date(str);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

export const toDateKey = (date) => {
  if (!date) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

export const addDays = (date, days) => {
  if (!date) return null;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
};

export const daysUntil = (value) => {
  const date = parseDateOnly(value);
  if (!date) return null;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((date - today) / 86400000);
};

export const formatDate = (value) => {
  const date = parseDateOnly(value);
  if (!date) return "";
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

// Expiration / due status for a date field.
// tone: "neutral" (not set) | "danger" (overdue) | "warn" (due soon) | "success"
export const dueStatus = (value, { soonDays = 30, overdueLabel = "Overdue", soonLabel = "Due soon" } = {}) => {
  const days = daysUntil(value);
  if (days === null) return { tone: "neutral", label: "Not set", days: null, rank: 0 };
  if (days < 0) {
    return {
      tone: "danger",
      label: `${overdueLabel} · ${Math.abs(days)}d`,
      days,
      rank: 3,
    };
  }
  if (days === 0) return { tone: "danger", label: "Due today", days, rank: 3 };
  if (days <= soonDays) return { tone: "warn", label: `${soonLabel} · ${days}d`, days, rank: 2 };
  return { tone: "success", label: "Current", days, rank: 1 };
};

export const normalizeUrl = (value) => {
  const str = String(value || "").trim();
  if (!str) return "";
  if (/^https?:\/\//i.test(str)) return str;
  return `https://${str}`;
};

export const displayUrl = (value) =>
  String(value || "").trim().replace(/^https?:\/\//i, "").replace(/\/$/, "");

export const copyText = async (text) => {
  const value = String(text ?? "");
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch (e) {
    // fall through to legacy path
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = value;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    document.execCommand("copy");
    document.body.removeChild(ta);
    return true;
  } catch (e) {
    return false;
  }
};

// ---------------------------------------------------------------------------
// Modal (portal + inline positioning)
// ---------------------------------------------------------------------------
export const OpsModal = ({
  open = true,
  title,
  subtitle,
  onClose,
  children,
  footer,
  width = 760,
  stacked = false,
  dismissOnOverlay = false,
}) => {
  if (!open) return null;
  const zOverlay = stacked ? 1999998 : 999998;
  const zBox = stacked ? 1999999 : 999999;

  return createPortal(
    <>
      <div
        onClick={dismissOnOverlay ? onClose : undefined}
        style={{
          position: "fixed",
          inset: 0,
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(15, 23, 42, 0.55)",
          zIndex: zOverlay,
        }}
      />
      <div
        role="dialog"
        aria-modal="true"
        className="opsui-root"
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: `min(${width}px, calc(100vw - 24px))`,
          maxHeight: "calc(100vh - 32px)",
          overflowY: "auto",
          background: "#ffffff",
          borderRadius: 14,
          boxShadow: "0 24px 60px rgba(15, 23, 42, 0.35)",
          zIndex: zBox,
          padding: 0,
          margin: 0,
          display: "block",
          textAlign: "left",
        }}
      >
        <div className="opsui-modal-head">
          <div style={{ minWidth: 0 }}>
            <h2 className="opsui-modal-title">{title}</h2>
            {subtitle && <p className="opsui-card-sub">{subtitle}</p>}
          </div>
          {onClose && (
            <button
              type="button"
              className="opsui-btn is-ghost is-icon"
              onClick={onClose}
              aria-label="Close"
              title="Close"
            >
              ✕
            </button>
          )}
        </div>
        <div className="opsui-modal-body">{children}</div>
        {footer && <div className="opsui-modal-foot">{footer}</div>}
      </div>
    </>,
    document.body
  );
};

export const ConfirmDialog = ({
  open = true,
  title = "Are you sure?",
  message,
  confirmLabel = "Delete",
  cancelLabel = "Cancel",
  onConfirm,
  onCancel,
  stacked = true,
  danger = true,
  busy = false,
}) => (
  <OpsModal
    open={open}
    title={title}
    onClose={onCancel}
    width={440}
    stacked={stacked}
    footer={
      <>
        <button type="button" className="opsui-btn" onClick={onCancel} disabled={busy}>
          {cancelLabel}
        </button>
        <button
          type="button"
          className={`opsui-btn ${danger ? "is-danger-solid" : "is-primary"}`}
          onClick={onConfirm}
          disabled={busy}
        >
          {busy ? "Working…" : confirmLabel}
        </button>
      </>
    }
  >
    <div style={{ fontSize: 14, color: "#334155", lineHeight: 1.5 }}>{message}</div>
  </OpsModal>
);

// ---------------------------------------------------------------------------
// Small building blocks
// ---------------------------------------------------------------------------
export const Pill = ({ tone = "neutral", plain = false, title, children }) => (
  <span className={`opsui-pill is-${tone}${plain ? " is-plain" : ""}`} title={title}>
    {children}
  </span>
);

export const statusTone = (status) => {
  const s = String(status || "").toLowerCase();
  if (!s) return "neutral";
  if (["active", "available", "arrived", "delivered", "received", "current"].includes(s)) return "success";
  if (["terminated", "canceled", "cancelled", "out of stock", "inactive"].includes(s)) return "danger";
  if (["pending", "low stock", "back-order", "on hold", "reserved"].includes(s)) return "warn";
  if (["ordered"].includes(s)) return "info";
  return "neutral";
};

export const StatusPill = ({ status, fallback = "—" }) =>
  status ? <Pill tone={statusTone(status)}>{status}</Pill> : <span className="opsui-muted">{fallback}</span>;

export const DuePill = ({ value, soonDays = 30, showDate = true, overdueLabel, soonLabel }) => {
  const s = dueStatus(value, { soonDays, overdueLabel, soonLabel });
  if (s.tone === "neutral") return <span className="opsui-muted">—</span>;
  return (
    <span style={{ display: "inline-flex", flexDirection: "column", gap: 3, alignItems: "flex-start" }}>
      <Pill tone={s.tone}>{s.label}</Pill>
      {showDate && <span className="opsui-cell-sub">{formatDate(value)}</span>}
    </span>
  );
};

export const Field = ({ label, required, hint, wide, children }) => (
  <div className={`opsui-field${wide ? " is-wide" : ""}`}>
    {label && (
      <label className="opsui-label">
        {label}
        {required && <span className="opsui-req">*</span>}
      </label>
    )}
    {children}
    {hint && <span className="opsui-hint">{hint}</span>}
  </div>
);

export const EmptyState = ({ title, children, action }) => (
  <div className="opsui-empty">
    <div className="opsui-empty-title">{title}</div>
    {children && <div style={{ fontSize: 14 }}>{children}</div>}
    {action && <div style={{ marginTop: 14 }}>{action}</div>}
  </div>
);

export const StateSelect = ({ name = "state", value, onChange, className = "opsui-select" }) => (
  <select name={name} value={value || ""} onChange={onChange} className={className}>
    <option value="">Select state</option>
    {US_STATES.map((s) => (
      <option key={s} value={s}>
        {s}
      </option>
    ))}
    {value && !US_STATES.includes(value) && <option value={value}>{value}</option>}
  </select>
);

// Password-style input with a show/hide toggle.
export const SecretInput = ({ name, value, onChange, placeholder, autoComplete = "new-password" }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="opsui-secret">
      <input
        className="opsui-input"
        type={show ? "text" : "password"}
        name={name}
        value={value || ""}
        onChange={onChange}
        placeholder={placeholder}
        autoComplete={autoComplete}
      />
      <button
        type="button"
        className="opsui-btn is-icon"
        onClick={() => setShow((v) => !v)}
        title={show ? "Hide" : "Show"}
        aria-label={show ? "Hide password" : "Show password"}
      >
        {show ? "🙈" : "👁"}
      </button>
    </div>
  );
};

// Read-only secret: masked by default, eye toggle + copy button.
export const SecretText = ({ value }) => {
  const [show, setShow] = useState(false);
  const [copied, setCopied] = useState(false);
  if (!value) return <span className="opsui-muted">—</span>;
  return (
    <span className="opsui-secret">
      <span className="opsui-secret-value">{show ? value : "•".repeat(Math.min(12, String(value).length || 8))}</span>
      <button
        type="button"
        className="opsui-btn is-icon is-sm"
        onClick={() => setShow((v) => !v)}
        title={show ? "Hide" : "Show"}
        aria-label={show ? "Hide password" : "Show password"}
      >
        {show ? "🙈" : "👁"}
      </button>
      <button
        type="button"
        className="opsui-btn is-icon is-sm"
        onClick={async () => {
          const ok = await copyText(value);
          if (ok) {
            setCopied(true);
            setTimeout(() => setCopied(false), 1400);
          }
        }}
        title="Copy"
      >
        {copied ? "Copied" : "Copy"}
      </button>
    </span>
  );
};

export const CopyButton = ({ value, label = "Copy" }) => {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  return (
    <button
      type="button"
      className="opsui-btn is-icon is-sm"
      onClick={async () => {
        const ok = await copyText(value);
        if (ok) {
          setCopied(true);
          setTimeout(() => setCopied(false), 1400);
        }
      }}
      title="Copy"
    >
      {copied ? "Copied" : label}
    </button>
  );
};

export const WebsiteLink = ({ value }) => {
  if (!value) return <span className="opsui-muted">—</span>;
  return (
    <a className="opsui-link" href={normalizeUrl(value)} target="_blank" rel="noopener noreferrer">
      {displayUrl(value)} ↗
    </a>
  );
};

export const SortHeader = ({ label, sortKey, sort, onSort, className = "" }) => {
  const active = sort?.key === sortKey;
  return (
    <th
      className={`is-sortable ${className}`}
      onClick={() => onSort(sortKey)}
      aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
    >
      {label}
      <span style={{ opacity: active ? 1 : 0.3, marginLeft: 4 }}>
        {active ? (sort.dir === "asc" ? "▲" : "▼") : "↕"}
      </span>
    </th>
  );
};

// ---------------------------------------------------------------------------
// Email reminder recipients for a module.
// Reads/writes notificationRecipients/{moduleKey} with the exact shape the
// NotificationRecipients page and the Cloud Functions use:
//   { userIds: string[], emails: string[], enabled: boolean }
// For vehicles it also lists staff who opted in through Staff Settings
// ("Receive Vehicle Expiration Emails" -> users.notificationPrefs.vehicles),
// which the backend merges into the same send.
// ---------------------------------------------------------------------------
export const ReminderRecipientsCard = ({
  moduleKey,
  title = "Email reminders",
  description,
  includeVehicleOptIns = false,
  schedule,
}) => {
  const [users, setUsers] = useState([]);
  const [settings, setSettings] = useState({ userIds: [], emails: [], enabled: true });
  const [loaded, setLoaded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ userIds: [], enabled: true });
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [usersSnap, recSnap] = await Promise.all([
          getDocs(collection(db, "users")),
          getDoc(doc(db, "notificationRecipients", moduleKey)),
        ]);
        if (cancelled) return;
        setUsers(usersSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        const data = recSnap.exists() ? recSnap.data() : {};
        setSettings({
          userIds: Array.isArray(data.userIds) ? data.userIds : [],
          emails: Array.isArray(data.emails) ? data.emails : [],
          enabled: data.enabled !== false,
        });
      } catch (e) {
        console.error(`Could not load ${moduleKey} reminder recipients:`, e);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [moduleKey]);

  const userName = (u) =>
    `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.name || u.email || "Unnamed";

  const listUsers = settings.userIds
    .map((id) => users.find((u) => u.id === id))
    .filter(Boolean);
  // emails saved without a matching user record (manually-entered / legacy)
  const extraEmails = settings.emails.filter(
    (email) => !listUsers.some((u) => String(u.email || "").toLowerCase() === String(email).toLowerCase())
  );

  const optInUsers = useMemo(
    () =>
      includeVehicleOptIns
        ? users.filter((u) => !isStaffDeactivated(u) && receivesVehicleExpirationEmails(u) && u.email)
        : [],
    [users, includeVehicleOptIns]
  );

  const activeUsers = useMemo(
    () =>
      users
        .filter((u) => !isStaffDeactivated(u) && u.email)
        .sort((a, b) => userName(a).localeCompare(userName(b))),
    [users]
  );

  const startEdit = () => {
    setDraft({ userIds: [...settings.userIds], enabled: settings.enabled });
    setFilter("");
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const userEmails = users
        .filter((u) => draft.userIds.includes(u.id))
        .map((u) => u.email)
        .filter(Boolean);
      // keep any legacy/manual emails that aren't tied to a user
      const keepExtra = extraEmails.filter(
        (email) => !userEmails.some((e) => e.toLowerCase() === String(email).toLowerCase())
      );
      const payload = {
        userIds: draft.userIds,
        emails: [...userEmails, ...keepExtra],
        enabled: draft.enabled,
      };
      await setDoc(doc(db, "notificationRecipients", moduleKey), payload, { merge: true });
      setSettings(payload);
      setEditing(false);
    } catch (e) {
      console.error("Could not save reminder recipients:", e);
      alert("Could not save reminder recipients.");
    } finally {
      setSaving(false);
    }
  };

  const totalRecipients = new Set(
    [...(settings.enabled ? settings.emails : []), ...optInUsers.map((u) => u.email)]
      .filter(Boolean)
      .map((e) => String(e).toLowerCase())
  ).size;

  return (
    <div className="opsui-card">
      <div className="opsui-card-head">
        <div>
          <h3 className="opsui-card-title">
            {title}{" "}
            {loaded &&
              (totalRecipients > 0 ? (
                <Pill tone="success">{totalRecipients} recipient{totalRecipients === 1 ? "" : "s"}</Pill>
              ) : (
                <Pill tone="warn">No recipients — emails will not send</Pill>
              ))}
          </h3>
          {description && <p className="opsui-card-sub">{description}</p>}
        </div>
        <button type="button" className="opsui-btn is-sm" onClick={startEdit} disabled={!loaded}>
          Manage recipients
        </button>
      </div>
      <div className="opsui-card-body" style={{ display: "grid", gap: 10 }}>
        <div>
          <div className="opsui-label" style={{ marginBottom: 6 }}>
            Reminder list {!settings.enabled && <Pill tone="danger">Paused</Pill>}
          </div>
          {listUsers.length || extraEmails.length ? (
            <div className="opsui-pills">
              {listUsers.map((u) => (
                <Pill key={u.id} tone="info" plain title={u.email}>
                  {userName(u)}
                </Pill>
              ))}
              {extraEmails.map((email) => (
                <Pill key={email} tone="neutral" plain>
                  {email}
                </Pill>
              ))}
            </div>
          ) : (
            <span className="opsui-muted" style={{ fontSize: 13 }}>
              Nobody on the list yet.
            </span>
          )}
        </div>
        {includeVehicleOptIns && (
          <div>
            <div className="opsui-label" style={{ marginBottom: 6 }}>
              Staff opted in (Staff Settings → “Receive Vehicle Expiration Emails”)
            </div>
            {optInUsers.length ? (
              <div className="opsui-pills">
                {optInUsers.map((u) => (
                  <Pill key={u.id} tone="neutral" plain title={u.email}>
                    {userName(u)}
                  </Pill>
                ))}
              </div>
            ) : (
              <span className="opsui-muted" style={{ fontSize: 13 }}>
                No staff have opted in.
              </span>
            )}
          </div>
        )}
        {schedule && (
          <div className="opsui-hint" style={{ fontSize: 12.5 }}>
            {schedule}
          </div>
        )}
      </div>

      {editing && (
        <OpsModal
          title={`${title} — recipients`}
          subtitle="Selected people receive the automatic reminder emails."
          onClose={() => setEditing(false)}
          width={520}
          footer={
            <>
              <button type="button" className="opsui-btn" onClick={() => setEditing(false)} disabled={saving}>
                Cancel
              </button>
              <button type="button" className="opsui-btn is-primary" onClick={save} disabled={saving}>
                {saving ? "Saving…" : "Save recipients"}
              </button>
            </>
          }
        >
          <label className="opsui-checkrow" style={{ marginBottom: 12 }}>
            <input
              type="checkbox"
              checked={draft.enabled}
              onChange={(e) => setDraft((p) => ({ ...p, enabled: e.target.checked }))}
            />
            Reminder list enabled
          </label>
          <input
            className="opsui-input"
            placeholder="Filter staff…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={{ marginBottom: 10 }}
          />
          <div
            style={{
              border: "1px solid #e2e8f0",
              borderRadius: 10,
              maxHeight: 320,
              overflowY: "auto",
              padding: 8,
            }}
          >
            {activeUsers
              .filter((u) =>
                `${userName(u)} ${u.email || ""}`.toLowerCase().includes(filter.trim().toLowerCase())
              )
              .map((u) => (
                <label key={u.id} className="opsui-checkrow" style={{ padding: "6px 4px" }}>
                  <input
                    type="checkbox"
                    checked={draft.userIds.includes(u.id)}
                    onChange={(e) =>
                      setDraft((p) => ({
                        ...p,
                        userIds: e.target.checked
                          ? [...p.userIds, u.id]
                          : p.userIds.filter((id) => id !== u.id),
                      }))
                    }
                  />
                  <span>
                    {userName(u)} <span className="opsui-muted" style={{ fontSize: 12 }}>{u.email}</span>
                  </span>
                </label>
              ))}
            {!activeUsers.length && <div className="opsui-muted">No staff with email addresses.</div>}
          </div>
        </OpsModal>
      )}
    </div>
  );
};
